import React, { useState, useEffect, useCallback, useMemo } from "react";
import { walkthroughAPI, schoolAPI } from "../services/api";
import "./WalkthroughPage.css";

// Default checklist items for every observation
const DEFAULT_CHECKLIST_KEYS = [
  { key: "objective_posted",         label: "Objective posted and visible" },
  { key: "aligned_to_teks",          label: "Aligned to TEKS standard" },
  { key: "higher_order_questioning", label: "Higher-order questioning observed" },
  { key: "student_engagement",       label: "Student engagement visible" },
];

const STATUS_CONFIG = {
  on_track: { label: "On Track",  cls: "on-track",  dotCls: "wt-dot-on-track",  badgeCls: "wt-badge-on-track"  },
  modified:  { label: "Modified",  cls: "modified",  dotCls: "wt-dot-modified",  badgeCls: "wt-badge-modified"  },
  enriched:  { label: "Enriched",  cls: "enriched",  dotCls: "wt-dot-enriched",  badgeCls: "wt-badge-enriched"  },
  no_entry:  { label: "No Entry",  cls: "no-entry",  dotCls: "wt-dot-no-entry",  badgeCls: "wt-badge-no-entry"  },
};

const getInitials = (name) =>
  String(name || "?")
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");

const formatDateDisplay = (dateStr) => {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-").map(Number);
  const d = new Date(Date.UTC(year, month - 1, day));
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

const todayKey = () => new Date().toISOString().split("T")[0];
const HISTORY_BATCH_SIZE = 15;

const shiftDate = (dateStr, days) => {
  const [year, month, day] = dateStr.split("-").map(Number);
  const d = new Date(Date.UTC(year, month - 1, day));
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().split("T")[0];
};

const periodRank = (label) => {
  const raw = String(label || "").trim();
  const numMatch = raw.match(/\d+/);
  if (numMatch) return Number(numMatch[0]);
  return Number.MAX_SAFE_INTEGER;
};

const sortPeriodLabels = (a, b) => {
  const numDelta = periodRank(a) - periodRank(b);
  if (numDelta !== 0) return numDelta;
  return String(a || "").localeCompare(String(b || ""));
};

// ─────────────────────────────────────────────────────────────────────────────
// ObservationPanel — checklist + notes + visibility toggle + save
// ─────────────────────────────────────────────────────────────────────────────
function ObservationPanel({ teacherId, date, schoolId, existingObservation, onSaved }) {
  const [checklist, setChecklist] = useState(() => {
    const base = {};
    DEFAULT_CHECKLIST_KEYS.forEach(({ key }) => { base[key] = false; });
    return { ...base, ...(existingObservation?.checklist || {}) };
  });
  const [notes, setNotes] = useState(existingObservation?.notes || "");
  const [isVisible, setIsVisible] = useState(existingObservation?.isVisibleToTeacher || false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Re-sync when teacher or date changes
  useEffect(() => {
    const base = {};
    DEFAULT_CHECKLIST_KEYS.forEach(({ key }) => { base[key] = false; });
    setChecklist({ ...base, ...(existingObservation?.checklist || {}) });
    setNotes(existingObservation?.notes || "");
    setIsVisible(existingObservation?.isVisibleToTeacher || false);
    setFeedback(null);
  }, [teacherId, date, existingObservation]);

  const toggleCheck = (key) => {
    setChecklist((prev) => ({ ...prev, [key]: !prev[key] }));
    setFeedback(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const data = {
        teacherId,
        date,
        schoolId,
        notes,
        checklist,
        isVisibleToTeacher: isVisible,
      };
      const result = existingObservation?.id
        ? await walkthroughAPI.updateObservation(existingObservation.id, {
            notes,
            checklist,
            isVisibleToTeacher: isVisible,
          })
        : await walkthroughAPI.saveObservation(data);
      const savedAt = result?.data?.updatedAt || result?.data?.createdAt || null;
      const savedAtLabel = savedAt
        ? new Date(savedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
        : "";
      setFeedback({
        type: "success",
        message: savedAtLabel ? `Observation saved at ${savedAtLabel}.` : "Observation saved.",
      });
      if (onSaved) onSaved(result.data);
    } catch (err) {
      const apiError = String(err?.response?.data?.error || "").trim();
      setFeedback({
        type: "error",
        message: apiError || "Could not save observation. Please try again.",
      });
      console.error("Failed to save observation:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wt-observation-form">
      <div className="wt-checklist">
        {DEFAULT_CHECKLIST_KEYS.map(({ key, label }) => (
          <div
            key={key}
            className="wt-checklist-item"
            onClick={() => toggleCheck(key)}
            role="checkbox"
            aria-checked={checklist[key]}
            tabIndex={0}
            onKeyDown={(e) => e.key === " " && toggleCheck(key)}
          >
            <div className={`wt-checklist-checkbox${checklist[key] ? " checked" : ""}`}>
              {checklist[key] && "✓"}
            </div>
            <span className="wt-checklist-label">{label}</span>
          </div>
        ))}
      </div>

      <div>
        <div className="wt-obs-notes-label">Observation Notes</div>
        <textarea
          className="wt-obs-textarea"
          value={notes}
          onChange={(e) => { setNotes(e.target.value); setFeedback(null); }}
          placeholder="Describe what you observed..."
          rows={4}
        />
      </div>

      <div className="wt-visibility-toggle">
        <span className="wt-visibility-label">Share notes with teacher</span>
        <label className="wt-toggle-switch">
          <input
            type="checkbox"
            checked={isVisible}
            onChange={(e) => { setIsVisible(e.target.checked); setFeedback(null); }}
          />
          <span className="wt-toggle-track" />
        </label>
      </div>

      <div className="wt-obs-actions">
        {feedback?.message ? (
          <span
            className={`wt-obs-feedback${feedback.type === "error" ? " is-error" : " is-success"}`}
            role="status"
            aria-live="polite"
          >
            {feedback.message}
          </span>
        ) : null}
        <button
          className="wt-save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save Observation"}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TeacherDetail — right panel content
// ─────────────────────────────────────────────────────────────────────────────
function TeacherDetail({ teacher, date, onBack }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savedObs, setSavedObs] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [historyError, setHistoryError] = useState("");

  const schoolId = teacher?.schoolId;
  const observationSchoolId = teacher?.schoolId || teacher?.fallbackSchoolId || "";

  const loadHistory = useCallback(
    async ({ offset = 0, append = false } = {}) => {
      if (!teacher?.teacherId) return;
      setHistoryLoading(true);
      setHistoryError("");
      try {
        const res = await walkthroughAPI.getTeacherPublishedPlans(teacher.teacherId, {
          schoolId: schoolId || undefined,
          limit: HISTORY_BATCH_SIZE,
          offset,
        });
        const payload = res?.data || {};
        const rows = Array.isArray(payload.rows) ? payload.rows : [];
        setHistoryRows((prev) => (append ? [...prev, ...rows] : rows));
        setHistoryHasMore(Boolean(payload.hasMore));
        setHistoryOffset(offset + rows.length);
      } catch (error) {
        console.error("Failed to load published plan history:", error);
        if (!append) setHistoryRows([]);
        setHistoryError("Could not load recent published plans.");
      } finally {
        setHistoryLoading(false);
      }
    },
    [schoolId, teacher?.teacherId],
  );

  useEffect(() => {
    if (!teacher) return;
    setLoading(true);
    setDetail(null);
    setSavedObs(null);
    walkthroughAPI
      .getTeacher(teacher.teacherId, {
        date,
        schoolId: schoolId || undefined,
        publishedOnly: true,
      })
      .then((res) => {
        setDetail(res.data);
        if (res.data?.observation) setSavedObs(res.data.observation);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [teacher, teacher?.teacherId, date, schoolId]);

  useEffect(() => {
    if (!teacher?.teacherId) return;
    setHistoryRows([]);
    setHistoryHasMore(false);
    setHistoryOffset(0);
    loadHistory({ offset: 0, append: false });
  }, [loadHistory, teacher?.teacherId, schoolId]);

  const handleObsSaved = (obs) => {
    setSavedObs(obs);
  };

  return (
    <div className="wt-detail-panel">
      <div className="wt-detail-scroll">
        <button className="wt-back-btn" onClick={onBack}>
          ← Back
        </button>

        <div className="wt-card">
          <div className="wt-detail-header">
            <div className="wt-detail-avatar">
              {getInitials(teacher?.teacherName)}
            </div>
            <div>
              <div className="wt-detail-teacher-name">{teacher?.teacherName}</div>
              <div className="wt-detail-teacher-email">{teacher?.teacherEmail}</div>
            </div>
          </div>
        </div>

        {loading && (
          <div className="wt-card" style={{ textAlign: "center", color: "#8899ad", padding: "32px" }}>
            Loading…
          </div>
        )}

        {!loading && detail && (
          <>
            <div className="wt-card">
              <div className="wt-card-title">Published Plans For {formatDateDisplay(date)}</div>
              {detail.periods.length === 0 ? (
                <div className="wt-no-lesson">No published lesson plans for this teacher on this day.</div>
              ) : (
                <div className="wt-period-plan-list">
                  {detail.periods.map((period, index) => {
                    const cfg = STATUS_CONFIG[period.status] || STATUS_CONFIG.no_entry;
                    return (
                      <div key={`${period.courseId}-${period.periodLabel}-${index}`} className="wt-period-plan-card">
                        <div className="wt-lesson-meta">
                          <span className={`wt-badge ${cfg.badgeCls}`}>{cfg.label}</span>
                          <span className="wt-period-chip">
                            {period.periodLabel} — {period.courseName}
                          </span>
                          {!schoolId && period.schoolName ? (
                            <span className="wt-teks-category-chip">{period.schoolName}</span>
                          ) : null}
                          {period.lesson?.teksCode ? (
                            <span className="wt-teks-chip">TEKS {period.lesson.teksCode}</span>
                          ) : null}
                        </div>

                        <div className="wt-lesson-title" style={{ marginTop: 10 }}>
                          {period.lesson?.lessonTitle || "No scheduled lesson"}
                        </div>
                        {period.lesson?.essentialQuestions ? (
                          <p className="wt-lesson-eq">{period.lesson.essentialQuestions}</p>
                        ) : null}

                        {period.plannerEntry ? (
                          <div className="wt-planner-block">
                            <div className="wt-planner-label">Published Teacher Plan</div>
                            {period.plannerEntry.publishedAt ? (
                              <p className="wt-planner-notes">
                                Published {new Date(period.plannerEntry.publishedAt).toLocaleString()}
                              </p>
                            ) : null}
                            {period.plannerEntry.objectiveOverride ? (
                              <p className="wt-planner-override">{period.plannerEntry.objectiveOverride}</p>
                            ) : null}
                            {period.plannerEntry.notes ? (
                              <p className="wt-planner-notes">{period.plannerEntry.notes}</p>
                            ) : null}
                            {(period.plannerEntry.hasStrategies || period.plannerEntry.hasResources) ? (
                              <div className="wt-planner-extras">
                                {period.plannerEntry.hasStrategies ? (
                                  <span className="wt-extra-chip">+ Strategies</span>
                                ) : null}
                                {period.plannerEntry.hasResources ? (
                                  <span className="wt-extra-chip">+ Resources</span>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <div className="wt-no-lesson">No published plan content for this class.</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="wt-card">
              <div className="wt-card-title">Recent Published Plans By Class</div>
              {historyError ? <p className="wt-no-lesson">{historyError}</p> : null}
              {historyRows.length === 0 && !historyLoading ? (
                <p className="wt-no-lesson">No published plan history found.</p>
              ) : null}
              {historyRows.length > 0 ? (
                <div className="wt-published-history-list">
                  {historyRows.map((row, index) => (
                    <div
                      key={`${row.calendarDate}-${row.courseId}-${row.periodLabel}-${index}`}
                      className="wt-published-history-item"
                    >
                      <div className="wt-published-history-head">
                        <strong>{formatDateDisplay(row.calendarDate)}</strong>
                        <span>{row.periodLabel} — {row.courseName}</span>
                        {!schoolId && row.schoolName ? <span>{row.schoolName}</span> : null}
                      </div>
                      {row.lessonTitle ? <p className="wt-lesson-eq">{row.lessonTitle}</p> : null}
                      {row.objectiveOverride ? <p className="wt-planner-override">{row.objectiveOverride}</p> : null}
                      {row.notes ? <p className="wt-planner-notes">{row.notes}</p> : null}
                      <p className="wt-detail-teacher-email" style={{ marginTop: 6 }}>
                        Published {new Date(row.publishedAt).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : null}
              {historyLoading ? <p className="wt-no-lesson">Loading published plans…</p> : null}
              {historyHasMore && !historyLoading ? (
                <button
                  type="button"
                  className="wt-load-more-btn"
                  onClick={() => loadHistory({ offset: historyOffset, append: true })}
                >
                  Load 15 More
                </button>
              ) : null}
            </div>

            <div className="wt-card">
              <div className="wt-card-title">Observation</div>
              <ObservationPanel
                teacherId={teacher?.teacherId}
                date={date}
                schoolId={observationSchoolId}
                existingObservation={savedObs}
                onSaved={handleObsSaved}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// WalkthroughPage — main page
// ─────────────────────────────────────────────────────────────────────────────
export default function WalkthroughPage({ authProfile }) {
  const [schools, setSchools] = useState([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState("");
  const [date, setDate] = useState(todayKey);
  const [teachers, setTeachers] = useState([]);
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState("");
  const [loadingTeacherOptions, setLoadingTeacherOptions] = useState(false);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [observationFilter, setObservationFilter] = useState(
    () => (authProfile?.role === "principal" ? "unobserved" : "all"),
  );
  const [periodFilter, setPeriodFilter] = useState("all");
  const [findView, setFindView] = useState("teacher");
  const [mobileShowDetail, setMobileShowDetail] = useState(false);

  const isPrincipal = authProfile?.role === "principal";
  const isAdmin = authProfile?.role === "admin";
  const effectiveSchoolId = selectedSchoolId || (isPrincipal && schools.length === 1 ? schools[0].id : "");

  const teacherById = useMemo(() => {
    const merged = new Map();
    [...teacherOptions, ...teachers].forEach((teacher) => {
      if (teacher?.teacherId) merged.set(teacher.teacherId, teacher);
    });
    return merged;
  }, [teacherOptions, teachers]);

  const teacherDropdownOptions = useMemo(
    () => [...teacherById.values()]
      .sort((a, b) => (a.teacherName || "").localeCompare(b.teacherName || "")),
    [teacherById],
  );

  const periodOptions = useMemo(() => {
    const labels = new Set();
    teacherDropdownOptions.forEach((teacher) => {
      (teacher?.periods || []).forEach((period) => {
        if (period?.periodLabel) labels.add(period.periodLabel);
      });
    });
    return [...labels].sort(sortPeriodLabels);
  }, [teacherDropdownOptions]);
  const hasPeriodLabels = periodOptions.length > 0;

  // Load schools list
  useEffect(() => {
    schoolAPI.getAll().then((res) => {
      setSchools(res.data || []);
      // Pre-select the first school (principals see only theirs from backend)
      if (res.data?.length === 1) setSelectedSchoolId(res.data[0].id);
    }).catch(console.error);
  }, []);

  // Load daily teacher list
  const loadDaily = useCallback(() => {
    if (isPrincipal && !effectiveSchoolId) {
      setTeachers([]);
      return;
    }
    setLoadingTeachers(true);
    walkthroughAPI
      .getDaily({
        schoolId: effectiveSchoolId || undefined,
        date,
        publishedOnly: true,
      })
      .then((res) => setTeachers(res.data || []))
      .catch(console.error)
      .finally(() => setLoadingTeachers(false));
  }, [isPrincipal, effectiveSchoolId, date]);

  // Load all teacher options for selected campus (assigned teachers, not just published)
  const loadTeacherOptions = useCallback(() => {
    if (isPrincipal && !effectiveSchoolId) {
      setTeacherOptions([]);
      return;
    }
    if (!isPrincipal && !effectiveSchoolId) {
      setTeacherOptions([]);
      return;
    }
    setLoadingTeacherOptions(true);
    walkthroughAPI
      .getDaily({
        schoolId: effectiveSchoolId || undefined,
        date,
        includeDraft: true,
        publishedOnly: false,
      })
      .then((res) => setTeacherOptions(res.data || []))
      .catch((error) => {
        console.error("Failed to load teacher options:", error);
        setTeacherOptions([]);
      })
      .finally(() => setLoadingTeacherOptions(false));
  }, [isPrincipal, effectiveSchoolId, date]);

  useEffect(() => { loadDaily(); }, [loadDaily]);
  useEffect(() => { loadTeacherOptions(); }, [loadTeacherOptions]);

  useEffect(() => {
    if (!selectedTeacherId) return;
    const teacher = teacherById.get(selectedTeacherId);
    if (!teacher) return;
    setSelectedTeacher({
      ...teacher,
      schoolId: effectiveSchoolId || teacher.schoolId || "",
      fallbackSchoolId: teacher.schoolId || "",
    });
  }, [selectedTeacherId, teacherById, effectiveSchoolId]);

  useEffect(() => {
    if (periodFilter === "all") return;
    if (periodOptions.includes(periodFilter)) return;
    setPeriodFilter("all");
  }, [periodFilter, periodOptions]);

  useEffect(() => {
    if (findView !== "period") return;
    if (hasPeriodLabels) return;
    setFindView("teacher");
  }, [findView, hasPeriodLabels]);

  // Status summary counts for legend
  const statusCounts = useMemo(() => {
    const counts = { on_track: 0, modified: 0, enriched: 0, no_entry: 0 };
    teachers.forEach((t) => {
      t.periods?.forEach((p) => {
        if (counts[p.status] !== undefined) counts[p.status]++;
      });
    });
    return counts;
  }, [teachers]);

  // Filtered + searched teacher list
  const filteredTeachers = useMemo(() => {
    let list = teachers;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (t) =>
          t.teacherName?.toLowerCase().includes(q) ||
          t.teacherEmail?.toLowerCase().includes(q) ||
          t.periods?.some((p) => p.courseName?.toLowerCase().includes(q)),
      );
    }
    if (statusFilter !== "all") {
      list = list.filter((t) =>
        t.periods?.some((p) => p.status === statusFilter),
      );
    }
    if (periodFilter !== "all") {
      list = list.filter((t) =>
        t.periods?.some((p) => p.periodLabel === periodFilter),
      );
    }
    if (observationFilter === "observed") {
      list = list.filter((t) => Boolean(t.observation));
    } else if (observationFilter === "unobserved") {
      list = list.filter((t) => !t.observation);
    }
    return list;
  }, [teachers, searchQuery, statusFilter, periodFilter, observationFilter]);

  const groupedByPeriod = useMemo(() => {
    const groups = new Map();
    filteredTeachers.forEach((teacher) => {
      const teacherPeriods = (teacher?.periods || [])
        .map((p) => p.periodLabel)
        .filter(Boolean);
      const labels = [...new Set(teacherPeriods)];
      labels.forEach((label) => {
        if (periodFilter !== "all" && label !== periodFilter) return;
        if (!groups.has(label)) groups.set(label, []);
        groups.get(label).push(teacher);
      });
    });

    return [...groups.entries()]
      .sort((a, b) => sortPeriodLabels(a[0], b[0]))
      .map(([periodLabel, rows]) => ({
        periodLabel,
        teachers: rows.sort((a, b) => (a.teacherName || "").localeCompare(b.teacherName || "")),
      }));
  }, [filteredTeachers, periodFilter]);

  const unobservedTeachers = useMemo(
    () => filteredTeachers.filter((teacher) => !teacher?.observation),
    [filteredTeachers],
  );

  const handleSelectTeacher = (t) => {
    setSelectedTeacherId(t.teacherId);
    setSelectedTeacher(t);
    setMobileShowDetail(true);
  };

  const handleNextUnobserved = useCallback(() => {
    if (!unobservedTeachers.length) return;
    const currentIndex = unobservedTeachers.findIndex((t) => t.teacherId === selectedTeacherId);
    const nextIndex = currentIndex >= 0
      ? (currentIndex + 1) % unobservedTeachers.length
      : 0;
    const nextTeacher = unobservedTeachers[nextIndex];
    handleSelectTeacher({
      ...nextTeacher,
      schoolId: effectiveSchoolId || "",
      fallbackSchoolId: nextTeacher.schoolId || "",
    });
  }, [unobservedTeachers, selectedTeacherId, effectiveSchoolId]);

  const handleTeacherDropdownChange = useCallback(async (event) => {
    const teacherId = event.target.value;
    setSelectedTeacherId(teacherId);

    if (!teacherId) {
      setSelectedTeacher(null);
      setMobileShowDetail(false);
      return;
    }

    const teacher = teacherById.get(teacherId);
    if (teacher) {
      setSelectedTeacher({
        ...teacher,
        schoolId: effectiveSchoolId || teacher.schoolId || "",
        fallbackSchoolId: teacher.schoolId || "",
      });
      setMobileShowDetail(true);
    }

    try {
      const latestRes = await walkthroughAPI.getTeacherPublishedPlans(teacherId, {
        schoolId: effectiveSchoolId || undefined,
        limit: 1,
        offset: 0,
      });
      const latestDate = latestRes?.data?.rows?.[0]?.calendarDate;
      if (latestDate) setDate(latestDate);
    } catch (error) {
      console.error("Failed to load teacher latest published lesson:", error);
    }
  }, [teacherById, effectiveSchoolId]);

  const handleBack = () => {
    setMobileShowDetail(false);
    setSelectedTeacher(null);
    setSelectedTeacherId("");
  };

  // Derive overall status for a teacher (worst case of all periods)
  const teacherOverallStatus = (t) => {
    const order = ["enriched", "modified", "on_track", "no_entry"];
    const statuses = t.periods?.map((p) => p.status) || [];
    for (const s of order) {
      if (statuses.includes(s)) return s;
    }
    return "no_entry";
  };

  const renderTeacherCard = (teacher, keyPrefix = "teacher") => {
    const status = teacherOverallStatus(teacher);
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.no_entry;
    const courses = [...new Set(teacher.periods?.map((p) => p.courseName))].join(", ");
    const isSelected = selectedTeacherId === teacher.teacherId;

    return (
      <div
        key={`${keyPrefix}-${teacher.teacherId}`}
        className={`wt-teacher-card${isSelected ? " selected" : ""}`}
        onClick={() =>
          handleSelectTeacher({
            ...teacher,
            schoolId: effectiveSchoolId || "",
            fallbackSchoolId: teacher.schoolId || "",
          })}
      >
        <div className="wt-teacher-avatar">{getInitials(teacher.teacherName)}</div>
        <div className="wt-teacher-info">
          <div className="wt-teacher-name">{teacher.teacherName}</div>
          <div className="wt-teacher-courses">{courses || "No courses"}</div>
        </div>
        <div className="wt-teacher-meta">
          <span className={`wt-status-dot ${cfg.dotCls}`} />
          {teacher.observation ? (
            <span className="wt-obs-icon" title="Observation logged">👁</span>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className="wt-page">
      {/* ── Top bar ── */}
      <div className="wt-topbar">
        <h1 className="wt-topbar-title">Walkthrough</h1>

        {(isAdmin || schools.length > 1) && (
          <select
            className="wt-school-select"
            value={selectedSchoolId}
            onChange={(e) => {
              setSelectedSchoolId(e.target.value);
              setSelectedTeacherId("");
              setSelectedTeacher(null);
              setMobileShowDetail(false);
            }}
          >
            <option value="">{isAdmin ? "All schools (admin)" : "Select a school…"}</option>
            {schools.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        )}

        {schools.length === 1 && !isAdmin && (
          <span style={{ fontWeight: 700, color: "#243143", fontSize: 15 }}>
            {schools[0]?.name}
          </span>
        )}

        {(isPrincipal || effectiveSchoolId) && (
          <select
            className="wt-teacher-select"
            value={selectedTeacherId}
            onChange={handleTeacherDropdownChange}
          >
            <option value="">
              {loadingTeacherOptions ? "Loading teachers…" : "Select teacher…"}
            </option>
            {teacherDropdownOptions.map((teacher) => (
              <option key={teacher.teacherId} value={teacher.teacherId}>
                {teacher.teacherName}
              </option>
            ))}
          </select>
        )}

        <button
          type="button"
          className="wt-next-teacher-btn"
          onClick={handleNextUnobserved}
          disabled={!unobservedTeachers.length}
          title="Jump to next teacher without an observation"
        >
          Next Unobserved ({unobservedTeachers.length})
        </button>

        {/* Date navigation */}
        <div className="wt-date-nav">
          <button className="wt-date-nav-btn" onClick={() => setDate((d) => shiftDate(d, -1))}>
            ‹
          </button>
          <span className="wt-date-label">{formatDateDisplay(date)}</span>
          <button className="wt-date-nav-btn" onClick={() => setDate((d) => shiftDate(d, 1))}>
            ›
          </button>
        </div>

        {date !== todayKey() && (
          <button className="wt-today-btn" onClick={() => setDate(todayKey())}>
            Today
          </button>
        )}

        <div className="wt-topbar-spacer" />

        {/* Status legend */}
        <div className="wt-status-legend">
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <span key={key} className="wt-legend-badge">
              <span className={`wt-status-dot ${cfg.dotCls}`} style={{ width: 8, height: 8, borderRadius: "50%", display: "inline-block" }} />
              {cfg.label} ({statusCounts[key] ?? 0})
            </span>
          ))}
        </div>
      </div>

      {/* ── Two-panel body ── */}
      <div className="wt-body">
        {/* LEFT PANEL */}
        <div className={`wt-list-panel${mobileShowDetail ? " hidden" : ""}`}>
          <div className="wt-list-filters">
            <input
              className="wt-search-input"
              type="search"
              placeholder="Search teachers or courses…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="wt-filter-row">
              <select
                className="wt-filter-select"
                value={findView}
                onChange={(e) => setFindView(e.target.value)}
              >
                <option value="teacher">Find By Teacher</option>
                {hasPeriodLabels ? <option value="period">Find By Period</option> : null}
              </select>
              <select
                className="wt-filter-select"
                value={observationFilter}
                onChange={(e) => setObservationFilter(e.target.value)}
              >
                <option value="all">All Observations</option>
                <option value="unobserved">Not Observed Yet</option>
                <option value="observed">Already Observed</option>
              </select>
            </div>
            <div className="wt-filter-row">
              {hasPeriodLabels ? (
                <select
                  className="wt-filter-select"
                  value={periodFilter}
                  onChange={(e) => setPeriodFilter(e.target.value)}
                >
                  <option value="all">All Periods</option>
                  {periodOptions.map((period) => (
                    <option key={period} value={period}>
                      {period}
                    </option>
                  ))}
                </select>
              ) : null}
              <select
                className="wt-filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="on_track">On Track</option>
                <option value="modified">Modified</option>
                <option value="enriched">Enriched</option>
                <option value="no_entry">No Entry</option>
              </select>
            </div>
          </div>

          <div className="wt-teacher-list">
            {loadingTeachers && (
              <div className="wt-list-empty">Loading teachers…</div>
            )}

            {!loadingTeachers && !selectedSchoolId && !isPrincipal && teachers.length > 0 && (
              <div className="wt-list-empty">Showing published plans across all schools.</div>
            )}

            {!loadingTeachers && filteredTeachers.length === 0 && (
              <div className="wt-list-empty">
                {teachers.length === 0
                  ? "No published teacher plans found for this date."
                  : "No teachers match your search."}
              </div>
            )}

            {!loadingTeachers && findView === "teacher" &&
              filteredTeachers.map((teacher) => renderTeacherCard(teacher))}

            {!loadingTeachers && findView === "period" &&
              groupedByPeriod.map((group) => (
                <div key={`group-${group.periodLabel}`} className="wt-period-group">
                  <div className="wt-period-group-header">
                    <span>{group.periodLabel}</span>
                    <span>{group.teachers.length} teacher{group.teachers.length === 1 ? "" : "s"}</span>
                  </div>
                  {group.teachers.map((teacher) =>
                    renderTeacherCard(teacher, `period-${group.periodLabel}`))}
                </div>
              ))}
          </div>
        </div>

        {/* RIGHT PANEL */}
        {selectedTeacher ? (
          <TeacherDetail
            teacher={selectedTeacher}
            date={date}
            onBack={handleBack}
          />
        ) : (
          <div className={`wt-detail-panel${mobileShowDetail ? "" : ""}`}>
            <div className="wt-detail-empty">
              {filteredTeachers.length > 0
                ? "Select a teacher to view lesson details and record an observation."
                : ""}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
